import React from 'react';
import { Avatar, Badge, InfoRow as SharedInfoRow } from 'components/common';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { LuWorkflow } from 'react-icons/lu';
import { FaExternalLinkAlt } from 'react-icons/fa';
import OplaCrmLink from 'components/app/crm/shared/OplaCrmLink';
// ── Helpers ──────────────────────────────────────────────────────────────────

const SectionHeading = ({ children }) => (
  <h6 className="tw-text-xs tw-font-bold tw-uppercase tw-tracking-widest tw-text-slate-400 tw-mb-4">
    {children}
  </h6>
);
SectionHeading.propTypes = { children: PropTypes.node.isRequired };

// Local label/value row retired in favour of the shared primitive — it was one
// of ~30 near-identical copies, and the shared one keeps the label gutter
// behind a breakpoint so it cannot squeeze the value on a phone again.
// `labelWidth="lg"` and the slate-800 value preserve this screen's look.
const InfoRow = ({ label, children }) => (
  <SharedInfoRow
    label={label}
    labelWidth="lg"
    divider
    labelClassName="tw-text-sm"
    valueClassName="tw-text-slate-800"
  >
    {children}
  </SharedInfoRow>
);
InfoRow.propTypes = {
  label: PropTypes.node.isRequired,
  children: PropTypes.node,
};

const getInitials = (name = '') =>
  name.split(' ').map(w => w[0]).join('').slice(0, 3).toUpperCase();

// ── Component ─────────────────────────────────────────────────────────────────

const Overview = ({ customer = {} }) => {
  const {
    name,
    parent,
    parent_id,
    type_of_company,
    legal_status,
    industry,
    source,
    employess,
    country_name,
    main_contact,
    secondary_contact,
    sales_unit,
    opla_id,
    user_avatar,
    created_at,
    updated_at,
  } = customer;

  return (
    <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-4">

      {/* ── Left column ─────────────────────────────────────────── */}
      <div className="tw-space-y-4">

        {/* Customer */}
        <div className="tw-bg-white tw-rounded-lg tw-border tw-border-slate-100 tw-shadow-sm tw-p-5">
          <SectionHeading>Customer</SectionHeading>
          <InfoRow label="Customer name">
            <span className="tw-font-medium tw-text-blue-600">{name}</span>
          </InfoRow>
          <InfoRow label="Parent">
            {parent?.name && (
              <Link
                to={`/crm/customers/${parent_id}`}
                target="_blank"
                className="tw-text-blue-600 tw-no-underline hover:tw-underline tw-flex tw-items-center tw-gap-1"
              >
                {parent.name}
                <FaExternalLinkAlt size={9} className="tw-opacity-50" />
              </Link>
            )}
          </InfoRow>
          <InfoRow label="Type of customer">
            {type_of_company && (
              <Badge variant="info" size="sm">{type_of_company}</Badge>
            )}
          </InfoRow>
          <InfoRow label="Legal status">
            {legal_status && (
              <Badge variant="primary" size="sm">{legal_status}</Badge>
            )}
          </InfoRow>
          <InfoRow label="Industry">{industry}</InfoRow>
          <InfoRow label="Source">{source}</InfoRow>
          <InfoRow label="No. of employees">{employess || '—'}</InfoRow>
          <InfoRow label="Country">{country_name}</InfoRow>
        </div>

        {/* Opla CRM */}
        <div className="tw-bg-white tw-rounded-lg tw-border tw-border-slate-100 tw-shadow-sm tw-p-5">
          <SectionHeading>
            <span className="tw-flex tw-items-center tw-gap-1.5">
              <LuWorkflow size={12} /> Opla CRM
            </span>
          </SectionHeading>
          <InfoRow label="Opla ID">
            {opla_id ? <OplaCrmLink type="customer" oplaId={opla_id} /> : null}
          </InfoRow>
        </div>
      </div>

      {/* ── Right column ────────────────────────────────────────── */}
      <div className="tw-space-y-4">

        {/* Responsible */}
        <div className="tw-bg-white tw-rounded-lg tw-border tw-border-slate-100 tw-shadow-sm tw-p-5">
          <SectionHeading>Responsible</SectionHeading>
          <InfoRow label="Main contact">
            {main_contact && (
              <span className="tw-font-medium tw-text-blue-600">{main_contact}</span>
            )}
          </InfoRow>
          <InfoRow label="Secondary contact">
            {secondary_contact?.length > 0 && (
              <div className="tw-flex tw-flex-wrap tw-gap-1">
                {secondary_contact.map((name, i) => (
                  <Badge key={i} variant="info" size="sm">{name}</Badge>
                ))}
              </div>
            )}
          </InfoRow>
          <InfoRow label="Sales unit">
            {sales_unit?.name && (
              <span className="tw-text-blue-600 tw-font-medium">
                {sales_unit.name}
                {sales_unit.description && (
                  <span className="tw-text-slate-400 tw-font-normal"> — {sales_unit.description}</span>
                )}
              </span>
            )}
          </InfoRow>
        </div>

        {/* History */}
        <div className="tw-bg-white tw-rounded-lg tw-border tw-border-slate-100 tw-shadow-sm tw-p-5">
          <SectionHeading>History</SectionHeading>
          <InfoRow label="Created by">
            {user_avatar && (
              <div className="tw-flex tw-items-center tw-gap-2">
                {user_avatar.avatar_url ? (
                  <Avatar
                    src={user_avatar.avatar_url}
                    size="sm"
                    alt={user_avatar.full_name}
                  />
                ) : (
                  <Avatar
                    initials={getInitials(user_avatar.full_name || user_avatar.name || '')}
                    size="sm"
                    variant="primary"
                  />
                )}
                <span className="tw-font-medium tw-text-slate-700">{user_avatar.full_name}</span>
              </div>
            )}
          </InfoRow>
          <InfoRow label="Created date">
            {created_at && (
              <span className="tw-text-slate-600">{created_at}</span>
            )}
          </InfoRow>
          <InfoRow label="Updated date">
            {updated_at && (
              <span className="tw-text-slate-600">{updated_at}</span>
            )}
          </InfoRow>
        </div>
      </div>

    </div>
  );
};

Overview.propTypes = {
  customer: PropTypes.object,
};

export default Overview;
